import { BrowserModule, HAMMER_GESTURE_CONFIG } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule, Injector, APP_INITIALIZER } from '@angular/core';
import { PlatformLocation } from '@angular/common';

import { StoreDevtoolsModule } from '@ngrx/store-devtools';

import { StoreRouterConnectingModule } from '@ngrx/router-store';

import { AbpHttpInterceptor } from 'config/abp-interceptor';
import { HTTP_INTERCEPTORS } from '@angular/common/http';

import { SharedModule } from '@shared/shared.module';
import { ServiceProxyModule } from '@shared/service-proxies/service-proxy.module';
import { RootRoutingModule } from './root-routing.module';

import { AppConsts } from '@shared/AppConsts';
import { AppSessionService } from '@shared/session/app-session.service';
import { API_BASE_URL } from '@shared/service-proxies/service-proxies';

import { RootComponent } from './root.component';
import { AppPreBootstrap } from './AppPreBootstrap';
import { HttpClientModule } from '@angular/common/http';

import { ToastrModule } from 'ngx-toastr';

import { GestureConfig } from '@angular/material';

import { CustomInterceptor } from 'config/customInterceptor';
import { WhiteLabelService } from '@shared/white-label/white-label.service';

import { StoreModule } from '@ngrx/store';
import { reducers } from './store';
import { EffectsModule } from '@ngrx/effects';
import { CommonEffects } from 'store/effects/common.effects';
import { MessagesEffects } from 'store/effects/messages.effects';
import { MsalModule, MsalInterceptor, MsalService } from '@azure/msal-angular';

import { alert } from './alert';

export function appInitializerFactory(injector: Injector, platformLocation: PlatformLocation) {
  return () => {
    return new Promise<boolean>((resolve, reject) => {
      AppConsts.appBaseHref = getBaseHref(platformLocation);
      const appBaseUrl = getDocumentOrigin() + AppConsts.appBaseHref;

      AppPreBootstrap.run(
        appBaseUrl,
        (args) => {
          const appSessionService: AppSessionService = injector.get(AppSessionService);
          const whiteLabelService: WhiteLabelService = injector.get(WhiteLabelService);
          whiteLabelService.setWhiteLabel(args.whiteLabel);
          appSessionService.init().then(
            (result) => {
              resolve(result);
            },
            (err) => {
              reject(err);
            }
          );
        },
        (e) => {
          const message = e.message || 'Error detail not sent by server.';
          alert.error('An error has occurred!', message);
          reject(e);
        }
      );
    });
  };
}

export function getRemoteServiceBaseUrl(): string {
  return AppConsts.remoteServiceBaseUrl;
}

@NgModule({
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    ToastrModule.forRoot({
      closeButton: false,
      positionClass: 'toast-top-center',
    }),
    SharedModule.forRoot(),
    ServiceProxyModule,
    RootRoutingModule,
    HttpClientModule,
    StoreModule.forRoot(reducers),
    StoreDevtoolsModule.instrument({
      maxAge: 25, // Retains last 25 states
    }),
    // Connects RouterModule with StoreModule, uses MinimalRouterStateSerializer by default
    StoreRouterConnectingModule.forRoot(),
    EffectsModule.forRoot([CommonEffects, MessagesEffects]),
    MsalModule.forRoot({
      auth: {
        clientId: AppConsts.ssoMsClientId,
        authority: `https://login.microsoftonline.com/common`,
        redirectUri: getDocumentOrigin()
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: true,
      }
    }),
  ],
  declarations: [RootComponent],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: CustomInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AbpHttpInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
    { provide: API_BASE_URL, useFactory: getRemoteServiceBaseUrl },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [Injector, PlatformLocation],
      multi: true,
    },
    { provide: HAMMER_GESTURE_CONFIG, useClass: GestureConfig },
  ],
  bootstrap: [RootComponent],
})
export class RootModule {}

export function getBaseHref(platformLocation: PlatformLocation): string {
  const baseUrl = platformLocation.getBaseHrefFromDOM();
  if (baseUrl) {
    return baseUrl;
  }

  return '/';
}

function getDocumentOrigin() {
  if (!document.location.origin) {
    const port = document.location.port ? ':' + document.location.port : '';
    return document.location.protocol + '//' + document.location.hostname + port;
  }

  return document.location.origin;
}
